import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockProjects, mockTaskLists, mockTasks, mockUsers, mockComments } from '../mock/data';
import { 
  Plus, ChevronDown, ChevronRight, Calendar, User, Clock, 
  MessageSquare, Paperclip, MoreHorizontal, Edit2, Trash2, 
  CheckCircle2, Circle, PlayCircle, X, Send
} from 'lucide-react';

// Task Hover Menu Component (Teamwork style)
const TaskHoverMenu = ({ task, position, onClose, onOpenComments, onStatusChange }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'in-progress': return <PlayCircle className="w-4 h-4 text-blue-500" />;
      default: return <Circle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  return (
    <div
      ref={menuRef}
      className="absolute z-50 bg-card border border-border rounded-lg shadow-xl p-2 min-w-[200px]"
      style={{ top: position.top, left: position.left }}
    >
      <div className="space-y-1">
        {/* Status options */}
        <p className="text-xs text-muted-foreground px-2 py-1 font-semibold">Change Status</p>
        <button
          onClick={() => onStatusChange('todo')}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted transition-colors text-foreground ${task.status === 'todo' ? 'bg-muted' : ''}`}
        >
          <Circle className="w-4 h-4 text-muted-foreground" />
          To Do
        </button>
        <button
          onClick={() => onStatusChange('in-progress')}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted transition-colors text-foreground ${task.status === 'in-progress' ? 'bg-muted' : ''}`}
        >
          <PlayCircle className="w-4 h-4 text-blue-500" />
          In Progress
        </button>
        <button
          onClick={() => onStatusChange('completed')}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted transition-colors text-foreground ${task.status === 'completed' ? 'bg-muted' : ''}`}
        >
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          Completed
        </button>
        
        <div className="border-t border-border my-2"></div>
        
        {/* Actions */}
        <button
          onClick={onOpenComments}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted transition-colors text-foreground"
        >
          <MessageSquare className="w-4 h-4" />
          Comments
        </button>
        <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted transition-colors text-foreground">
          <Edit2 className="w-4 h-4" />
          Edit Task
        </button>
        <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-destructive/10 text-destructive transition-colors">
          <Trash2 className="w-4 h-4" />
          Delete Task
        </button>
      </div>
    </div>
  );
};

// Comments Panel Component (Slide-in panel like Teamwork)
const CommentsPanel = ({ task, isOpen, onClose }) => {
  const [comment, setComment] = useState('');
  const taskComments = mockComments.filter(c => c.taskId === task?.id) || [];
  const project = mockProjects.find(p => p.id === task?.projectId);

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      
      {/* Panel */}
      <div className="relative w-full max-w-md bg-card border-l border-border h-full shadow-xl animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
          <div>
            <h3 className="font-semibold text-foreground">{task.title}</h3>
            <p className="text-sm text-muted-foreground">{project?.name}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Task Progress */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-semibold text-foreground">
              {task.status === 'completed' ? '100' : task.status === 'in-progress' ? '50' : '0'}%
            </span>
          </div>
          <Progress 
            value={task.status === 'completed' ? 100 : task.status === 'in-progress' ? 50 : 0} 
            className="h-2"
          />
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: 'calc(100vh - 280px)' }}>
          {taskComments.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No comments yet</p>
              <p className="text-sm text-muted-foreground">Be the first to comment</p>
            </div>
          ) : (
            taskComments.map(comment => {
              const user = mockUsers.find(u => u.id === comment.userId);
              return (
                <div key={comment.id} className="flex space-x-3">
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback>{user?.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-foreground">{user?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{comment.content}</p>
                    {comment.attachments?.length > 0 && (
                      <div className="mt-2 flex items-center space-x-2 text-xs text-muted-foreground">
                        <Paperclip className="w-3 h-3" />
                        <span>{comment.attachments.length} attachment(s)</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Add Comment */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
          <div className="flex gap-2">
            <Textarea
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[60px] bg-muted/50"
              rows={2}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <Button variant="outline" size="sm">
              <Paperclip className="w-4 h-4 mr-2" />
              Attach
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => {
                if (comment.trim()) {
                  alert('Comment will be saved with backend integration');
                  setComment('');
                }
              }}
            >
              <Send className="w-4 h-4 mr-2" />
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskList = () => {
  const { projectId } = useParams();
  const project = mockProjects.find(p => p.id === projectId) || mockProjects[0];
  const projectTaskLists = mockTaskLists.filter(tl => tl.projectId === project.id);
  const [expandedLists, setExpandedLists] = useState(projectTaskLists.map(tl => tl.id));
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Hover menu state
  const [hoverMenuTask, setHoverMenuTask] = useState(null);
  const [hoverMenuPosition, setHoverMenuPosition] = useState({ top: 0, left: 0 });
  
  // Comments panel state
  const [commentsPanelTask, setCommentsPanelTask] = useState(null);

  const toggleList = (listId) => {
    setExpandedLists(prev =>
      prev.includes(listId) ? prev.filter(id => id !== listId) : [...prev, listId]
    );
  };

  const handleTaskHover = (e, task) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220; // approximate menu width
    const viewportWidth = window.innerWidth;
    
    // Position menu to the left of the button if it would overflow
    let left = rect.right + 10;
    if (left + menuWidth > viewportWidth) {
      left = rect.left - menuWidth - 10;
    }
    
    setHoverMenuPosition({
      top: rect.top + window.scrollY,
      left: left
    });
    setHoverMenuTask(task);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'in-progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'todo': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'in-progress': return <PlayCircle className="w-5 h-5 text-blue-500" />;
      default: return <Circle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: project.color }}></div>
            <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
          </div>
          <p className="text-muted-foreground mt-1">{project.description}</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Task Lists */}
      <div className="space-y-6">
        {projectTaskLists.map((taskList) => {
          const listTasks = mockTasks.filter(t => t.taskListId === taskList.id);
          const completedCount = listTasks.filter(t => t.status === 'completed').length;
          const isExpanded = expandedLists.includes(taskList.id);

          return (
            <Card key={taskList.id} className="bg-card border-border overflow-hidden">
              <CardContent className="p-0">
                {/* List Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                  onClick={() => toggleList(taskList.id)}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{taskList.name}</h3>
                      <p className="text-sm text-muted-foreground">{taskList.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-muted-foreground">
                      {completedCount}/{listTasks.length} completed
                    </span>
                    <Button variant="ghost" size="icon" onClick={(e) => {
                      e.stopPropagation();
                      alert('Add task to this list');
                    }}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Tasks */}
                {isExpanded && (
                  <div>
                    {listTasks.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground">
                        <p className="text-sm">No tasks in this list yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {listTasks.map((task) => {
                          const assignee = mockUsers.find(u => u.id === task.assigneeId);
                          const taskComments = mockComments.filter(c => c.taskId === task.id);
                          
                          return (
                            <div
                              key={task.id}
                              className="group p-4 hover:bg-muted/30 transition-colors flex items-center gap-4"
                              data-testid={`task-item-${task.id}`}
                            >
                              {/* Status Icon / Checkbox */}
                              <button
                                onClick={() => alert('Status change will be saved with backend')}
                                className="flex-shrink-0 hover:scale-110 transition-transform"
                                data-testid={`task-status-${task.id}`}
                              >
                                {getStatusIcon(task.status)}
                              </button>

                              {/* Task Content */}
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-medium text-foreground ${task.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                                  {task.title}
                                </h4>
                                <div className="flex items-center flex-wrap gap-2 mt-1">
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Calendar className="w-3 h-3" />
                                    <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                                  </div>
                                  {task.tags.slice(0, 2).map(tag => (
                                    <Badge key={tag} variant="outline" className="text-xs text-foreground">{tag}</Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Task Meta */}
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <Badge className={`${getStatusColor(task.status)} text-xs`}>{task.status}</Badge>
                                <Badge className={`${getPriorityColor(task.priority)} text-xs`}>{task.priority}</Badge>
                                
                                {/* Assignee */}
                                <Avatar className="w-7 h-7">
                                  <AvatarImage src={assignee?.avatar} />
                                  <AvatarFallback className="text-xs">{assignee?.name.charAt(0)}</AvatarFallback>
                                </Avatar>

                                {/* Time logged */}
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Clock className="w-4 h-4" />
                                  <span>{task.loggedHours}/{task.estimatedHours}h</span>
                                </div>

                                {/* Comment Icon - Opens comment panel */}
                                <button
                                  onClick={() => setCommentsPanelTask(task)}
                                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted"
                                  data-testid={`task-comments-btn-${task.id}`}
                                >
                                  <MessageSquare className="w-4 h-4" />
                                  {taskComments.length > 0 && (
                                    <span className="text-xs">{taskComments.length}</span>
                                  )}
                                </button>

                                {/* More Options - Opens hover menu */}
                                <button
                                  onClick={(e) => handleTaskHover(e, task)}
                                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100"
                                  data-testid={`task-menu-btn-${task.id}`}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Hover Menu */}
      {hoverMenuTask && (
        <TaskHoverMenu
          task={hoverMenuTask}
          position={hoverMenuPosition}
          onClose={() => setHoverMenuTask(null)}
          onOpenComments={() => {
            setCommentsPanelTask(hoverMenuTask);
            setHoverMenuTask(null);
          }}
          onStatusChange={(status) => {
            alert(`Status will be changed to: ${status}`);
            setHoverMenuTask(null);
          }}
        />
      )}

      {/* Comments Side Panel */}
      <CommentsPanel
        task={commentsPanelTask}
        isOpen={!!commentsPanelTask}
        onClose={() => setCommentsPanelTask(null)}
      />

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-foreground">Task Title</Label>
              <Input placeholder="Enter task title" className="mt-1" />
            </div>
            <div>
              <Label className="text-foreground">Description</Label>
              <Textarea placeholder="Task description" className="mt-1" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Task List</Label>
                <Select>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select list" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTaskLists.map(list => (
                      <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Assignee</Label>
                <Select>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Priority</Label>
                <Select>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Estimated Hours</Label>
                <Input type="number" placeholder="Hours" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Start Date</Label>
                <Input type="date" className="mt-1" />
              </div>
              <div>
                <Label className="text-foreground">Due Date</Label>
                <Input type="date" className="mt-1" />
              </div>
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => {
              alert('Task creation will be implemented with backend');
              setCreateDialogOpen(false);
            }}>
              Create Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskList;
